import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

export interface ApiError extends Error {
  status: number
  body?: unknown
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Skip auth redirect on 401 (e.g. login request itself) */
  skipAuthRedirect?: boolean
  /** Skip global toast on error */
  silent?: boolean
  /** Override timeout (ms); 0 disables */
  timeout?: number
  /** Tag for grouping related calls in dev tools / cache */
  tag?: string
}

const DEFAULT_TIMEOUT = 30_000

function buildError(status: number, message: string, body?: unknown): ApiError {
  const err = new Error(message) as ApiError
  err.name = 'ApiError'
  err.status = status
  err.body = body
  return err
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http') ? path : path.startsWith('/') ? path : `/${path}`
  const url = new URL(base, window.location.origin)
  if (query) {
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined || v === null) return
      url.searchParams.set(k, String(v))
    })
  }
  return url.pathname + url.search
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const {
    body,
    query,
    skipAuthRedirect,
    silent,
    timeout = DEFAULT_TIMEOUT,
    headers,
    signal,
    ...rest
  } = options

  const auth = useAuthStore()
  const notify = useNotificationStore()

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(headers ?? {}),
    },
    ...rest,
  }

  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      init.body = body
    } else {
      (init.headers as Record<string, string>)['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }
  }

  const token = auth.getToken()
  if (token) {
    ;(init.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const url = buildUrl(path, query)

  const controller = new AbortController()
  let externalAbort = false
  if (signal) {
    if (signal.aborted) {
      controller.abort()
      externalAbort = true
    } else {
      signal.addEventListener('abort', () => {
        externalAbort = true
        controller.abort()
      })
    }
  }

  let timer: number | undefined
  if (timeout > 0) {
    timer = window.setTimeout(() => controller.abort(), timeout)
  }

  let response: Response
  try {
    response = await fetch(url, { ...init, signal: controller.signal })
  } catch (err) {
    if (timer) window.clearTimeout(timer)
    const isAbort = (err as { name?: string }).name === 'AbortError'
    if (isAbort && externalAbort) throw err
    const message = isAbort ? '请求超时' : (err as Error).message ?? '网络错误'
    if (!silent) notify.error(message, '网络错误')
    throw buildError(0, message)
  }

  if (timer) window.clearTimeout(timer)

  // 401 — redirect to login (unless explicitly skipped)
  if (response.status === 401 && !skipAuthRedirect) {
    auth.clearAuth()
    if (!silent) notify.warning('登录已过期，请重新登录', '会话失效')
    if (window.location.hash !== '#/login') {
      window.location.hash = '#/login'
    }
    throw buildError(401, 'Unauthorized')
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')

  let parsed: unknown = null
  if (isJson) {
    try {
      parsed = await response.json()
    } catch {
      parsed = null
    }
  } else if (response.body) {
    parsed = await response.text()
  }

  if (!response.ok) {
    const message = extractErrorMessage(parsed, response.status)
    if (!silent) notify.error(message, `错误 ${response.status}`)
    throw buildError(response.status, message, parsed)
  }

  return parsed as T
}

// extractErrorMessage 从错误响应体里取出可展示给用户的文案。
//
// Go 侧有两种写法并存: sendError 发 {"error": ..., "code": ...},
// 而 webui_config / remove-device 之类发 {"message": ...}。此前这里只
// 认 message, 于是所有走 sendError 的端点 (包括配对失败这种最需要看清
// 原因的) 错误 toast 全都退化成"请求失败 (status)"。
function extractErrorMessage(parsed: unknown, status: number): string {
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>
    for (const key of ['message', 'error'] as const) {
      const value = obj[key]
      if (typeof value === 'string' && value.trim() !== '') return value
    }
  }
  return `请求失败 (${status})`
}

const api = {
  get: <T>(path: string, opts: RequestOptions = {}) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts: RequestOptions = {}) =>
    request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, opts: RequestOptions = {}) => request<T>('DELETE', path, opts),
}

export default api
